import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
// @ts-ignore
import Keycloak from 'keycloak-js';

interface AuthContextType {
  keycloak: any;
  initialized: boolean;
  authenticated: boolean;
  keycloakOnline: boolean;
  user: {
    username: string;
    email: string;
    name: string;
  } | null;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  keycloak: null,
  initialized: false,
  authenticated: false,
  keycloakOnline: true,
  user: null,
  login: () => {},
  logout: () => {},
});

const keycloakConfig = {
  url: 'https://auth.sempara.de',
  realm: 'sayses-system',
  clientId: 'sayses-admin-app',
};

// Check if we have a cached session
const getCachedUser = () => {
  try {
    const cached = localStorage.getItem('sayses_admin_user');
    if (cached) {
      const data = JSON.parse(cached);
      // Check if token is not expired (with 5 min buffer)
      if (data.exp && data.exp * 1000 > Date.now() + 300000) {
        return data.user;
      }
    }
  } catch (e) {
    // Ignore
  }
  return null;
};

const setCachedUser = (user: any, exp: number) => {
  try {
    localStorage.setItem('sayses_admin_user', JSON.stringify({ user, exp }));
  } catch (e) {
    // Ignore
  }
};

const clearCachedUser = () => {
  try {
    localStorage.removeItem('sayses_admin_user');
  } catch (e) {
    // Ignore
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [keycloak, setKeycloak] = useState<any>(null);
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [keycloakOnline, setKeycloakOnline] = useState(true);
  const [user, setUser] = useState<AuthContextType['user']>(null);

  useEffect(() => {
    const kc = new Keycloak(keycloakConfig);

    // Try to initialize with check-sso first (non-blocking)
    kc.init({
      onLoad: 'check-sso',
      checkLoginIframe: false,
      pkceMethod: 'S256',
      silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
    })
      .then((auth: boolean) => {
        setKeycloak(kc);
        setKeycloakOnline(true);
        setInitialized(true);

        if (auth && kc.tokenParsed) {
          const userData = {
            username: kc.tokenParsed.preferred_username || '',
            email: kc.tokenParsed.email || '',
            name: kc.tokenParsed.name || kc.tokenParsed.preferred_username || '',
          };
          setUser(userData);
          setAuthenticated(true);
          setCachedUser(userData, kc.tokenParsed.exp || 0);

          // Token refresh
          setInterval(() => {
            kc.updateToken(70).catch(() => {
              console.log('Failed to refresh token');
              // Don't logout immediately - keep using cached session
            });
          }, 60000);
        } else {
          // Not authenticated - redirect to login
          kc.login();
        }
      })
      .catch((error: any) => {
        console.error('Keycloak init error:', error);
        setKeycloakOnline(false);

        // Keycloak is offline - check for cached session
        const cachedUser = getCachedUser();
        if (cachedUser) {
          console.log('Using cached session');
          setUser(cachedUser);
          setAuthenticated(true);
        }

        setInitialized(true);
      });
  }, []);

  const login = () => {
    if (keycloak) {
      keycloak.login();
    } else {
      // Keycloak not available
      alert('Authentifizierungsserver nicht erreichbar. Bitte versuchen Sie es später erneut.');
    }
  };

  const logout = () => {
    clearCachedUser();
    if (keycloak?.authenticated) {
      keycloak.logout({ redirectUri: window.location.origin });
    } else {
      setAuthenticated(false);
      setUser(null);
      window.location.reload();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        keycloak,
        initialized,
        authenticated,
        keycloakOnline,
        user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
